import { clsx } from 'clsx'
import { AlertCircle, ArrowLeft, CheckCircle, Clock, Mail, MousePointer } from 'lucide-react'
import { Link, useParams } from 'react-router'
import { type EmailEvent, useEmail } from '@/api/hooks/use-emails'
import { StatusBadge } from '@/components/ui/status-badge'
import { diffMs, formatDateTime, formatMs } from '@/lib/format'

const EVENT_COLORS: Record<string, string> = {
  'email.queued': 'bg-gray-400',
  'email.sent': 'bg-blue-500',
  'email.delivered': 'bg-green-500',
  'email.bounced': 'bg-red-500',
  'email.complained': 'bg-orange-500',
  'email.opened': 'bg-emerald-500',
  'email.clicked': 'bg-cyan-500',
  'email.rejected': 'bg-red-600',
  'email.delayed': 'bg-yellow-500',
}

const EVENT_ICONS: Record<string, React.ReactNode> = {
  'email.queued': <Clock className="h-3 w-3 text-white" />,
  'email.sent': <Mail className="h-3 w-3 text-white" />,
  'email.delivered': <CheckCircle className="h-3 w-3 text-white" />,
  'email.bounced': <AlertCircle className="h-3 w-3 text-white" />,
  'email.complained': <AlertCircle className="h-3 w-3 text-white" />,
  'email.opened': <Mail className="h-3 w-3 text-white" />,
  'email.clicked': <MousePointer className="h-3 w-3 text-white" />,
  'email.rejected': <AlertCircle className="h-3 w-3 text-white" />,
  'email.delayed': <Clock className="h-3 w-3 text-white" />,
}

function EventTimeline({ events }: { events: EmailEvent[] }): React.JSX.Element {
  return (
    <ol className="relative ml-3 border-l border-surface-3">
      {events.map((event) => {
        const eventName = event.type.replace('email.', '')
        return (
          <li key={`${event.type}-${event.timestamp}`} className="mb-6 ml-6">
            <span
              className={clsx(
                'absolute -left-2.5 flex h-5 w-5 items-center justify-center rounded-full ring-4 ring-surface-0',
                EVENT_COLORS[event.type] ?? 'bg-gray-400',
              )}
            >
              {EVENT_ICONS[event.type] ?? <Clock className="h-3 w-3 text-white" />}
            </span>
            <h3 className="text-sm font-medium capitalize text-text-primary">{eventName}</h3>
            <time className="text-xs text-text-tertiary">{formatDateTime(event.timestamp)}</time>
            {event.transport && (
              <p className="mt-1 text-xs text-text-secondary">via {event.transport}</p>
            )}
            {event.url && <p className="mt-1 truncate text-xs text-brand-600">{event.url}</p>}
          </li>
        )
      })}
    </ol>
  )
}

export function EmailDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>()
  const { data: email, isLoading } = useEmail(id ?? '')

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    )
  }

  if (!email) {
    return (
      <div className="py-24 text-center">
        <p className="text-lg font-medium text-text-primary">Email not found</p>
      </div>
    )
  }

  const latency =
    email.delivered_at && email.created_at
      ? formatMs(diffMs(email.created_at, email.delivered_at))
      : null

  return (
    <div className="space-y-6">
      <Link
        to="/emails"
        className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Emails
      </Link>

      {/* Header info */}
      <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
        <h1 className="text-xl font-semibold text-text-primary">Email {email.id}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-text-secondary">
          <div className="flex items-center gap-2">
            <span>Status:</span>
            <StatusBadge status={email.status} />
          </div>
          {email.transport && (
            <div className="flex items-center gap-2">
              <span>Transport:</span>
              <StatusBadge status={email.transport} />
            </div>
          )}
          {email.ip && (
            <div>
              <span>IP: </span>
              <span className="font-mono">{email.ip}</span>
            </div>
          )}
          {email.to[0] && (
            <div>
              <span>Recipient: </span>
              <span className="font-mono">{email.to[0]}</span>
            </div>
          )}
          {latency && (
            <div>
              <span>Latency: </span>
              <span className="font-medium">{latency}</span>
            </div>
          )}
          {email.opens > 0 && (
            <div>
              <span>Opens: </span>
              <span className="font-medium">{email.opens}</span>
            </div>
          )}
          {email.clicks > 0 && (
            <div>
              <span>Clicks: </span>
              <span className="font-medium">{email.clicks}</span>
            </div>
          )}
        </div>
      </div>

      {/* Event timeline */}
      <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
        <h2 className="mb-4 text-sm font-medium text-text-secondary">Event Timeline</h2>
        {email.events.length > 0 ? (
          <EventTimeline events={email.events} />
        ) : (
          <p className="text-sm text-text-tertiary">No events recorded yet.</p>
        )}
      </div>
    </div>
  )
}
